import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Youtube, RefreshCw, Plus, Loader2, Sparkles, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { CaseCombobox } from "@/components/CaseCombobox";

function VideoRow({ v, cases, onImported }) {
  const [caseId, setCaseId] = useState(v.suggested_case_id || "");
  const [saving, setSaving] = useState(false);
  const isSuggested = v.suggested_case_id && caseId === v.suggested_case_id;

  const add = async () => {
    if (!caseId) { toast.error("Pick a case first"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/youtube/import", {
        case_id: caseId, youtube_id: v.youtube_id, title: v.title, description: v.description || "",
      });
      if (data.imported) toast.success("Added to case");
      else toast.info("Already on that case");
      onImported(v.youtube_id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 border-b border-white/5">
      <img src={v.thumbnail} alt="" className="w-28 h-16 rounded-sm object-cover shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white font-medium line-clamp-2">{v.title}</p>
        <p className="label-mono mt-0.5">{(v.published || "").slice(0, 10)}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
        <div className="w-56">
          <CaseCombobox cases={cases} value={caseId} onChange={setCaseId} placeholder="Assign to case…" />
          {isSuggested && <span className="mt-1 flex items-center gap-1 text-[10px] font-mono text-gold/80" data-testid={`yt-suggested-${v.youtube_id}`}><Sparkles className="w-3 h-3" /> Suggested match</span>}
        </div>
        <button
          onClick={add} disabled={saving}
          data-testid={`yt-import-${v.youtube_id}`}
          className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-gold/40 text-gold hover:bg-gold/10 transition-colors disabled:opacity-50 whitespace-nowrap"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Add
        </button>
      </div>
    </div>
  );
}

export function YouTubeSync({ cases = [] }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  const load = (force = false) => {
    setLoading(true); setErr(false);
    api.get(`/youtube/channel-latest${force ? "?force=true" : ""}`)
      .then(({ data }) => setVideos(data.videos || []))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Only show videos that aren't already on a case
  const pending = videos.filter((v) => !v.imported);
  const onImported = (youtubeId) =>
    setVideos((vs) => vs.map((v) => (v.youtube_id === youtubeId ? { ...v, imported: true } : v)));

  const bulkSync = async () => {
    const toAdd = pending.filter((v) => v.suggested_case_id);
    if (toAdd.length === 0) { toast.info("No suggested matches to sync"); return; }
    let ok = 0;
    for (const v of toAdd) {
      try {
        await api.post("/admin/youtube/import", {
          case_id: v.suggested_case_id, youtube_id: v.youtube_id, title: v.title, description: v.description || "",
        });
        onImported(v.youtube_id);
        ok += 1;
      } catch { /* skip failures, keep going */ }
    }
    toast.success(`Synced ${ok} suggested video${ok === 1 ? "" : "s"} to their cases`);
  };

  const suggestedCount = pending.filter((v) => v.suggested_case_id).length;

  return (
    <div className="card-surface rounded-md p-5 mb-6" data-testid="youtube-sync-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2"><Youtube className="w-4 h-4 text-gold" /><h3 className="font-display font-semibold text-white">Sync from YouTube</h3></div>
        <div className="flex items-center gap-2">
          {suggestedCount > 0 && (
            <button onClick={bulkSync} data-testid="yt-bulk-sync-btn"
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm bg-gold/15 border border-gold/40 text-gold hover:bg-gold/25 transition-colors">
              <Sparkles className="w-3.5 h-3.5" /> Sync all suggested ({suggestedCount})
            </button>
          )}
          <button onClick={() => load(true)} disabled={loading} data-testid="yt-refresh-btn"
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border border-white/10 text-white/70 hover:border-gold/40 transition-colors disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>
      <p className="text-xs text-white/50 mb-3">Your latest channel uploads that aren&apos;t on a case yet. Assign one to a case to publish it there — added videos drop off this list.</p>
      {loading ? (
        <div className="flex items-center gap-2 text-white/40 text-sm py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading channel…</div>
      ) : err ? (
        <div className="text-sm text-white/50 py-6 text-center">Couldn&apos;t reach YouTube. <button onClick={() => load(true)} className="text-gold underline">Try again</button></div>
      ) : pending.length === 0 ? (
        <div className="flex flex-col items-center gap-2 text-white/40 py-8 text-center" data-testid="yt-all-caught-up">
          <CheckCircle2 className="w-6 h-6 text-gold/70" />
          <span className="text-sm">All caught up — every recent upload is already on a case.</span>
        </div>
      ) : (
        <div className="rounded-sm border border-white/5 max-h-[28rem] overflow-y-auto">
          {pending.map((v) => <VideoRow key={v.youtube_id} v={v} cases={cases} onImported={onImported} />)}
        </div>
      )}
    </div>
  );
}
